import type {
  ContinuousScrollerFilter,
  DataGridColumnSettings,
  DataGridSettings,
  DropDownResult,
  MobileFilterInputChangeEvent,
  MobileFilterType,
  MobileFilterValueType,
  OdinDataRetrieval,
  SearchValueType,
} from '@myosh/odin-components';
import {
  Button,
  convertComparisonTypeToString,
  isDropDownResult,
  MobileDateFilter,
  MobileMultiSelectDropDownFilter,
  MobileNumericFilter,
  OdinIcon,
  OdinIconSize,
  TextSearchField,
} from '@myosh/odin-components';
import { isDate, isEqual } from 'lodash';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FieldFilters } from '../../@types/common';
import { convertToFieldFilters } from './menu/data-grid-menu-utils';
import type { MobileCardsFilterRef } from './mobile-filter-utils';
import { getContinuousScrollerFilters, isFilterSupported } from './mobile-filter-utils';
export interface MobileFilterProps {
  setUserFilters: (filters: FieldFilters) => void;
  gridSettings?: DataGridSettings;
  gridSettingsLoading?: boolean;
  filterData?: OdinDataRetrieval;
}

function MobileCardsFilter(props: MobileFilterProps, ref: React.Ref<MobileCardsFilterRef>) {
  const { setUserFilters, gridSettings, gridSettingsLoading, filterData } = props;
  const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
  // initial value calculated from grid settings or cleared value
  const [componentFilters, setComponentFilters] = useState<MobileFilterType | undefined>();
  const gridColumnsSettings = useRef<DataGridColumnSettings[]>([]);
  const initialFieldFilter = useRef<ContinuousScrollerFilter[]>([]);
  // filters that will be applied if user confirms selection
  // local state of component filters
  const localUserFilters = useRef<MobileFilterType>({});
  const latestAppliedFilters = useRef<MobileFilterType>({});
  const { t } = useTranslation();

  const resetFilterState = useCallback(
    (columns?: DataGridColumnSettings[], initialFieldFilters: ContinuousScrollerFilter[] = []) => {
      if (columns) {
        gridColumnsSettings.current = columns;
        initialFieldFilter.current = initialFieldFilters;
        const initialFilters = transformFromGridSettings(columns, initialFieldFilters);
        latestAppliedFilters.current = initialFilters;
        setUserFilters(convertFilters(initialFilters));
        setComponentFilters(initialFilters);
        localUserFilters.current = initialFilters;
      }
    },
    [setUserFilters]
  );

  useImperativeHandle(
    ref,
    () => ({
      resetToInitial: resetFilterState,
    }),
    [resetFilterState]
  );

  useEffect(() => {
    if (!gridSettingsLoading && gridSettings?.columns && !isEqual(gridColumnsSettings.current, gridSettings.columns)) {
      resetFilterState(gridSettings.columns, gridSettings.initialFieldFilters);
    } else if (
      gridSettings?.initialFieldFilters &&
      !gridSettingsLoading &&
      !isEqual(initialFieldFilter.current, gridSettings.initialFieldFilters)
    ) {
      const updatedLocalFilters = {
        ...componentFilters,
        ...convertToFieldFilters(gridSettings.initialFieldFilters ?? []),
      };

      latestAppliedFilters.current = updatedLocalFilters;
      setUserFilters(convertFilters(updatedLocalFilters));
      setComponentFilters(updatedLocalFilters);
      localUserFilters.current = updatedLocalFilters;
      initialFieldFilter.current = gridSettings?.initialFieldFilters || [];
    }
  }, [gridSettings?.columns, gridSettings?.initialFieldFilters, gridSettingsLoading, resetFilterState]);

  const onInputChange = useCallback(
    ({ column, key, value, comparison = 0, removeEmpty = true }: MobileFilterInputChangeEvent) => {
      const isEmpty = value === undefined || (typeof value === 'string' && value?.length === 0);
      let newFilters: MobileFilterType;
      if (removeEmpty && isEmpty) {
        newFilters = { ...localUserFilters.current };
        delete newFilters[key];
        localUserFilters.current = newFilters;
        return;
      }
      const filterValue = convertForFilterValue(value, isEmpty);
      newFilters = {
        ...localUserFilters.current,
        [key]: {
          value: filterValue,
          comparison: convertComparisonTypeToString(comparison),
          customDataProperties: { ...column.customDataProperties, originalComparison: comparison },
        },
      };
      if (isEmpty) {
        const currentFilters = { ...localUserFilters.current };
        delete currentFilters[key];
        localUserFilters.current = currentFilters;
      } else {
        localUserFilters.current = newFilters;
      }
    },
    []
  );

  const filterComponent = useCallback(
    (column: DataGridColumnSettings) => {
      let key = column.id as string | number;
      if (column.customDataProperties) {
        const { entityType, entityPropertyName, entityId } = column.customDataProperties;
        if (entityType === 'KEYWORD' && entityPropertyName) {
          key = entityPropertyName.toString();
        } else if (entityId) {
          key = entityId.toString();
        }
      }
      switch (column.searchType) {
        case 'TextSearch':
          return (
            <TextSearchField
              id={String(key)}
              currentValue={(componentFilters?.[key]?.value as string) ?? (column.searchValue as string) ?? ''}
              onInputChanged={(value) => onInputChange({ column, key, value, comparison: column.searchComparison })}
            />
          );
        case 'MultiSelect':
          return (
            <MobileMultiSelectDropDownFilter
              search={filterData}
              column={column}
              onInputChange={onInputChange}
              id={key}
              componentFilters={componentFilters}
            />
          );
        case 'Integer':
        case 'Decimal':
          return (
            <MobileNumericFilter
              onInputChange={onInputChange}
              column={column}
              filterValue={componentFilters?.[key]}
              id={key}
            />
          );
        case 'DateRange':
          return (
            <MobileDateFilter
              column={column}
              filterValue={componentFilters?.[key]}
              id={key}
              onInputChange={onInputChange}
            />
          );
        default:
          return <></>;
      }
    },
    [componentFilters]
  );

  const labelComponent = (column: DataGridColumnSettings) => {
    const className = 'odin-shrink odin-pr-2 odin-truncate';
    const textValue = column.title || '';
    const columnId = String(column.id);

    if (column.searchType === 'TextSearch') {
      return (
        <label className={className} htmlFor={columnId}>
          {textValue}
        </label>
      );
    }

    return <p className={className}>{textValue}</p>;
  };

  const displayedFilters = useMemo(() => {
    return gridSettings?.columns?.filter((column) => isFilterSupported(column.searchType)) ?? [];
  }, [gridSettings]);

  const clearAllSection = useMemo(() => {
    return (
      <div className="border-gray-200 bg-gray-50 flex justify-end border-b p-1">
        <Button
          style={{ width: 'fit-content' }}
          size="small"
          type="text"
          onClick={() => {
            localUserFilters.current = {};
            setComponentFilters({});
          }}
        >
          {t('clear-all-filters', 'Clear All Filters')}
        </Button>
      </div>
    );
  }, [t]);

  const footer = useMemo(() => {
    return (
      <div className="border-gray-200 bg-gray-50 flex flex-shrink-0 justify-end gap-3 border-t p-4">
        <Button
          onClick={() => {
            setIsFilterPopoverOpen(false);
            // reset to the latest approved filters
            localUserFilters.current = latestAppliedFilters.current;
            setComponentFilters(latestAppliedFilters.current);
          }}
        >
          {t('cancel')}
        </Button>
        <Button
          variant="regular"
          type="primary"
          onClick={() => {
            setIsFilterPopoverOpen(false);
            latestAppliedFilters.current = localUserFilters.current;
            setComponentFilters(localUserFilters.current);
            setUserFilters(convertFilters(localUserFilters.current));
          }}
        >
          {t('apply')}
        </Button>
      </div>
    );
  }, []);

  return (
    <>
      <div className="ml-auto h-fit" onClick={() => setIsFilterPopoverOpen(!isFilterPopoverOpen)}>
        <OdinIcon
          icon="ListFilter"
          size={OdinIconSize.Medium}
          className="odin-cursor-pointer odin-rounded-md flex size-8 bg-gray-5 px-2 text-primary-1 hover:bg-primary-5"
        />
      </div>
      {isFilterPopoverOpen && (
        <div
          className="fixed right-0 top-0 z-40 flex h-dvh w-screen flex-col bg-mono-1 outline-none transition-all"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex w-full flex-grow flex-col overflow-y-auto">
            {clearAllSection}
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              <div className="space-y-4">
                {displayedFilters.map((column) => {
                  return (
                    <div key={column.id} className="mb-2 space-y-1">
                      <div className="text-gray-700 text-sm font-medium">{labelComponent(column)}</div>
                      <div className="w-full">{filterComponent(column)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            {footer}
          </div>
        </div>
      )}
    </>
  );
}

export default forwardRef(MobileCardsFilter);

const convertForFilterValue = (value?: MobileFilterValueType, isEmpty?: boolean) => {
  if (isEmpty || value === undefined) {
    return '';
  }
  if (Array.isArray(value) || isDropDownResult(value) || isDate(value)) {
    return value;
  }
  return String(value);
};

export const convertFilters = (filters: MobileFilterType): FieldFilters => {
  const result: FieldFilters = {};
  Object.keys(filters).forEach((key) => {
    let filterValue: SearchValueType | Array<SearchValueType>;
    const originalValue = filters[key].value;
    if (isDropDownResult(originalValue)) {
      const dropdownResult = originalValue as DropDownResult;
      filterValue = dropdownResult.value as SearchValueType;
    } else if (Array.isArray(originalValue) && originalValue.length > 0 && isDropDownResult(originalValue[0])) {
      filterValue = originalValue.map((item) => (item as DropDownResult).value as SearchValueType);
    } else {
      filterValue = originalValue as SearchValueType | Array<SearchValueType>;
    }

    result[key] = {
      value: filterValue,
      comparison: filters[key].comparison,
      customDataProperties: filters[key].customDataProperties,
    };
  });
  return result;
};

export const transformFromGridSettings = (
  columns: DataGridColumnSettings[],
  initialFieldFilters: ContinuousScrollerFilter[] = []
): FieldFilters => {
  return convertToFieldFilters(getContinuousScrollerFilters(columns, initialFieldFilters));
};
